import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Box,
  Paper,
  TextField,
  Typography,
  Button,
  CircularProgress,
  LinearProgress,
  InputAdornment,
  Alert,
  AlertTitle,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CloudQueueIcon from "@mui/icons-material/CloudQueue";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import {
  refineWorkflow,
  getWorkflowWebhookConfig,
  getWorkflowScheduleConfig,
  configureWorkflowSchedule,
  configureWorkflowWebhook,
  deleteWorkflowWebhookConfig,
} from "../../api/agentApi.js";
import { CronScheduleBuilder, getScheduleLabel } from "./CronScheduleBuilder";
import { WebhookConfigBuilder } from "./WebhookConfigBuilder";
import WorkflowVisualization from "./WorkflowVisualization";

interface RefinementMessage {
  id: string;
  text: string;
  timestamp: Date;
  type: "user" | "agent";
}

type WorkflowTriggerMode = "manual" | "schedule" | "webhook";

interface WorkflowTriggerDraft {
  mode: WorkflowTriggerMode;
  schedule_config?: { cron_expression: string; enabled: boolean } | null;
  webhook_config?: { adapter_type: string; enabled: boolean; secrets_ref: Record<string, string> } | null;
}

interface WorkflowReviewProps {
  workflowName: string;
  onWorkflowNameChange?: (name: string) => void;
  dagDefinition: any;
  isEdit: boolean;
  isCreating: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
  intent: string;
  onIntentChange?: (newIntent: string) => void;
  /** Intent history from the generate step — seeded into accumulatedIntentHistory so refinements append to it. */
  initialIntentHistory?: any[] | null;
  /** Called when the refine API returns an updated DAG. Parent should update dagDefinition. */
  onDagRefined?: (
    updatedDag: any,
    updatedName: string,
    intentHistory?: any[] | null,
  ) => void;
  /**
   * Optional override for refinement execution. When provided, the editor calls this
   * instead of the stateless refineWorkflow() API. Useful in edit mode to call the
   * in-place PUT /workflow/{id}/refine endpoint that saves as a new version immediately.
   * Should return { updatedDag, updatedName } on success, or throw on failure.
   */
  onRefinementRequested?: (
    refinementText: string,
  ) => Promise<{ updatedDag: any; updatedName: string }>;
  /**
   * Edit mode only: called when the user explicitly saves a name change.
   * Parent should persist the new name (e.g. via PUT /workflow/{id}).
   */
  onWorkflowNameSave?: (newName: string) => Promise<void>;
  /** Saved workflow UUID, used to load and save trigger configuration in edit mode. */
  workflowId?: string;
  /** Pre-fetched schedule config from the parent (avoids an extra API roundtrip in edit mode). */
  initialScheduleConfig?: any;
  /** Create mode only: suggested schedule detected from intent (shown as an info banner). */
  suggestedScheduleConfig?: any;
  /** Create mode only: suggested webhook detected from intent (shown as an info banner). */
  suggestedWebhookConfig?: any;
  /** Create mode: passes the selected trigger draft back so it can be applied after creation. */
  onTriggerDraftChange?: (draft: WorkflowTriggerDraft) => void;
}

const WorkflowRefinementEditor: React.FC<WorkflowReviewProps> = ({
  workflowName,
  onWorkflowNameChange,
  dagDefinition,
  isEdit,
  isCreating,
  onConfirm,
  onCancel,
  intent,
  onIntentChange,
  initialIntentHistory,
  onDagRefined,
  onRefinementRequested,
  onWorkflowNameSave,
  workflowId,
  initialScheduleConfig,
  suggestedScheduleConfig,
  suggestedWebhookConfig,
  onTriggerDraftChange,
}) => {
  const [isRefining, setIsRefining] = useState(false);
  const [isSavingName, setIsSavingName] = useState(false);
  // Seeded from generate-phase history; grows with each pre-save refinement turn
  const [accumulatedIntentHistory, setAccumulatedIntentHistory] = useState<
    any[] | null
  >(initialIntentHistory ?? null);
  // Name field — seeded once on mount, never overwritten by LLM refinements
  const [nameInputValue, setNameInputValue] = useState(workflowName);
  // Tracks the last successfully persisted name (used as the undo baseline)
  const savedNameRef = React.useRef(workflowName);
  // Track whether the user has unsaved name changes (edit mode only)
  const [nameChanged, setNameChanged] = useState(false);

  // ── Trigger configuration ────────────────────────────────────────────────
  const initialTriggerMode: WorkflowTriggerMode = suggestedWebhookConfig?.adapter_type
    ? "webhook"
    : initialScheduleConfig?.cron_expression || suggestedScheduleConfig?.cron_expression
      ? "schedule"
      : "manual";
  const [triggerMode, setTriggerMode] = useState<WorkflowTriggerMode>(initialTriggerMode);
  const [scheduleConfig, setScheduleConfig] = useState<any>(null);
  const [scheduleCron, setScheduleCron] = useState(
    initialScheduleConfig?.cron_expression ??
      suggestedScheduleConfig?.cron_expression ??
      "0 9 * * 1-5",
  );
  const [scheduleEnabled, setScheduleEnabled] = useState(
    Boolean(initialScheduleConfig?.enabled ?? suggestedScheduleConfig?.cron_expression),
  );
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [scheduleMsg, setScheduleMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const [webhookConfig, setWebhookConfig] = useState<any>(null);
  const [webhookEditable, setWebhookEditable] = useState<{ adapter_type: string; secrets: Record<string, string> }>({
    adapter_type: suggestedWebhookConfig?.adapter_type ?? "",
    secrets: suggestedWebhookConfig?.secrets_ref ?? {},
  });
  const [webhookEnabled, setWebhookEnabled] = useState(Boolean(suggestedWebhookConfig?.enabled));
  const [webhookSaving, setWebhookSaving] = useState(false);
  const [webhookMsg, setWebhookMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [webhookRemoving, setWebhookRemoving] = useState(false);
  const shouldValidateWebhook =
    triggerMode === "webhook" ||
    webhookEnabled ||
    Boolean(webhookEditable.adapter_type);

  useEffect(() => {
    if (isEdit) return;
    if (suggestedScheduleConfig?.cron_expression) {
      setTriggerMode("schedule");
      setScheduleCron(suggestedScheduleConfig.cron_expression);
      setScheduleEnabled(true);
      return;
    }
    if (suggestedWebhookConfig?.adapter_type) {
      setTriggerMode("webhook");
      setWebhookEditable({
        adapter_type: suggestedWebhookConfig.adapter_type,
        secrets: suggestedWebhookConfig.secrets_ref ?? {},
      });
      setWebhookEnabled(Boolean(suggestedWebhookConfig.enabled));
    }
  }, [isEdit, suggestedScheduleConfig, suggestedWebhookConfig]);

  useEffect(() => {
    onTriggerDraftChange?.({
      mode: triggerMode,
      schedule_config:
        triggerMode === "schedule"
          ? { cron_expression: scheduleCron, enabled: scheduleEnabled }
          : null,
      webhook_config:
        triggerMode === "webhook" && webhookEditable.adapter_type
          ? {
              adapter_type: webhookEditable.adapter_type,
              enabled: webhookEnabled,
              secrets_ref: webhookEditable.secrets,
            }
          : null,
    });
  }, [onTriggerDraftChange, triggerMode, scheduleCron, scheduleEnabled, webhookEditable, webhookEnabled]);

  useEffect(() => {
    if (!isEdit || !workflowId) return;

    // Seed immediately from prop if available (avoids visible "Not configured" flash)
    if (initialScheduleConfig?.cron_expression) {
      setScheduleConfig(initialScheduleConfig);
      setScheduleCron(initialScheduleConfig.cron_expression);
      setScheduleEnabled(!!initialScheduleConfig.enabled);
      setTriggerMode("schedule");
    }

    // Also refresh from API in case the version changed since navigation
    getWorkflowScheduleConfig(workflowId)
      .then((sc: any) => {
        if (sc?.cron_expression) {
          setScheduleConfig(sc);
          setScheduleCron(sc.cron_expression);
          setScheduleEnabled(!!sc.enabled);
          setTriggerMode("schedule");
        }
      })
      .catch(() => {/* no schedule configured for current version */});
    // Load webhook config via API
    getWorkflowWebhookConfig(workflowId)
      .then((cfg: any) => {
        if (cfg?.adapter_type) {
          setWebhookConfig(cfg);
          setWebhookEnabled(!!cfg.enabled);
          const rawSecrets = cfg.secrets_ref ?? {};
          const safeSecrets: Record<string, string> = Object.fromEntries(
            Object.entries(rawSecrets).map(([k, v]) => [k, typeof v === "string" ? v : ""])
          );
          setWebhookEditable({ adapter_type: cfg.adapter_type ?? "", secrets: safeSecrets });
          setTriggerMode((current) => (current === "schedule" ? current : "webhook"));
        }
      })
      .catch(() => {/* no webhook configured */});
  }, [isEdit, workflowId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveSchedule = async () => {
    if (!workflowId) return;
    setScheduleSaving(true);
    setScheduleMsg(null);
    try {
      const saved = await configureWorkflowSchedule(workflowId, { cron_expression: scheduleCron, enabled: scheduleEnabled });
      setScheduleConfig(saved);
      setScheduleMsg({ type: "success", text: "Schedule saved." });
      setTimeout(() => setScheduleMsg(null), 3000);
    } catch (e: any) {
      setScheduleMsg({ type: "error", text: e?.message ?? "Failed to save schedule." });
    } finally {
      setScheduleSaving(false);
    }
  };

  const handleSaveWebhook = async () => {
    if (!workflowId || !webhookEditable.adapter_type) return;
    setWebhookSaving(true);
    setWebhookMsg(null);
    try {
      const saved = await configureWorkflowWebhook(workflowId, { enabled: webhookEnabled, adapter_type: webhookEditable.adapter_type, secrets_ref: webhookEditable.secrets });
      setWebhookConfig(saved);
      setWebhookMsg({ type: "success", text: "Webhook configuration saved." });
      setTimeout(() => setWebhookMsg(null), 3000);
    } catch (e: any) {
      setWebhookMsg({ type: "error", text: e?.message ?? "Failed to save webhook config." });
    } finally {
      setWebhookSaving(false);
    }
  };

  const handleRemoveWebhook = async () => {
    if (!workflowId) return;
    setWebhookRemoving(true);
    setWebhookMsg(null);
    try {
      await deleteWorkflowWebhookConfig(workflowId);
      setWebhookConfig(null);
      setWebhookEnabled(false);
      setWebhookEditable({ adapter_type: "", secrets: {} });
      setWebhookMsg({ type: "success", text: "Webhook trigger removed." });
      setTimeout(() => setWebhookMsg(null), 3000);
    } catch (e: any) {
      setWebhookMsg({ type: "error", text: e?.message ?? "Failed to remove webhook config." });
    } finally {
      setWebhookRemoving(false);
    }
  };

  const draftScheduleConfig =
    triggerMode === "schedule"
      ? { cron_expression: scheduleCron, enabled: scheduleEnabled }
      : null;
  const draftWebhookConfig =
    triggerMode === "webhook" && webhookEditable.adapter_type
      ? {
          adapter_type: webhookEditable.adapter_type,
          enabled: webhookEnabled,
          secrets_ref: webhookEditable.secrets,
        }
      : null;

  const [chatHistory, setChatHistory] = useState<RefinementMessage[]>(
    // Don't seed an empty bubble — only add the initial message if there's actual intent text
    intent.trim()
      ? [{ id: "initial", text: intent, timestamp: new Date(), type: "user" }]
      : [],
  );
  const [inputValue, setInputValue] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  // Detect PLACEHOLDER or missing agent_ids in the current DAG — shown as a warning banner
  const placeholderSteps = useMemo(() => {
    const steps: any[] = dagDefinition?.steps ?? [];
    return steps
      .filter((s) => {
        if (s.type !== "agent") return false;
        const id = (s.agent_id ?? "").trim();
        return (
          !id ||
          id.toUpperCase() === "PLACEHOLDER" ||
          id.toUpperCase() === "PENDING" ||
          /^\{\{.*\}\}$/.test(id)   // mustache template placeholder e.g. {{classifier_agent_id}}
        );
      })
      .map((s) => s.id as string);
  }, [dagDefinition]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [chatHistory]);

  const handleSendRefinement = async () => {
    if (!inputValue.trim() || isRefining) return;

    const refinementText = inputValue.trim();
    const userMessage: RefinementMessage = {
      id: Date.now().toString(),
      text: refinementText,
      timestamp: new Date(),
      type: "user",
    };
    setChatHistory((prev) => [...prev, userMessage]);
    setInputValue("");

    if (onDagRefined) {
      setIsRefining(true);
      try {
        let updatedDag: any;
        let freshHistory: any[] | null = null;

        if (onRefinementRequested) {
          // Delegate to parent (e.g. in-place save via PUT /workflow/{id}/refine)
          const result = await onRefinementRequested(refinementText);
          updatedDag = result.updatedDag;
          // result.updatedName is intentionally ignored — name is user-owned
        } else {
          // Stateless refine (new workflow creation flow)
          // Pass accumulated history so the backend appends each turn correctly
          const result = await refineWorkflow(
            intent,
            refinementText,
            dagDefinition,
            20,
            accumulatedIntentHistory as any,
          );
          updatedDag = result?.dag_definition ?? result;
          // result.name is intentionally ignored — name is user-owned after first mount
          // Capture the fresh intent_history returned by the backend
          freshHistory = result?.intent_history ?? null;
          if (freshHistory) {
            setAccumulatedIntentHistory(freshHistory);
          }
        }

        // Notify parent to update dagDefinition and intent_history.
        // Intentionally do NOT forward updatedName — the name field is user-owned
        // after the editor first opens and must not be overwritten by LLM suggestions.
        onDagRefined(updatedDag, workflowName, freshHistory);

        const agentMessage: RefinementMessage = {
          id: (Date.now() + 1).toString(),
          text: isEdit
            ? `✅ Workflow saved: "${refinementText}"`
            : `Workflow updated: "${refinementText}"`,
          timestamp: new Date(),
          type: "agent",
        };
        setChatHistory((prev) => [...prev, agentMessage]);
      } catch (e: any) {
        const errMsg =
          e?.message ?? "Failed to refine workflow. Please try again.";
        const errorMessage: RefinementMessage = {
          id: (Date.now() + 1).toString(),
          text: `⚠️ ${errMsg}`,
          timestamp: new Date(),
          type: "agent",
        };
        setChatHistory((prev) => [...prev, errorMessage]);
      } finally {
        setIsRefining(false);
      }
    } else if (onIntentChange) {
      // Fallback: just propagate the text (no API call)
      onIntentChange(refinementText);
    }
  };

  const handleSaveName = async (newName: string) => {
    if (!onWorkflowNameSave || !newName.trim() || isSavingName) return;
    setIsSavingName(true);
    try {
      await onWorkflowNameSave(newName.trim());
    } finally {
      setIsSavingName(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        maxWidth: "100%",
        height: "calc(100vh - 220px)",
        maxHeight: "calc(100vh - 220px)",
        overflow: "hidden",
        boxSizing: "border-box",
        bgcolor: "#f8fafc",
      }}
    >
      {/* Main content row */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          flex: 1,
          minHeight: 0,
          width: "100%",
          maxWidth: "100%",
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        {/* Left Panel: Persistent Refinement Log */}
        <Box
          sx={{
            width: "30%",
            flexShrink: 0,
            flexGrow: 0,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            borderRight: "1px solid #e2e8f0",
            height: "100%",
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          <Box sx={{ p: 2, borderBottom: "1px solid #e2e8f0" }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: "#334155", textAlign: "center" }}
            >
              {isEdit ? "Update Workflow" : "Workflow Refinement"}
            </Typography>
            {isEdit && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  textAlign: "center",
                  color: "#64748B",
                  mt: 0.5,
                }}
              >
                Describe your changes below and click <strong>Update</strong> —
                the workflow is saved immediately.
              </Typography>
            )}
          </Box>

          {/* Scrollable Chat Area */}
          <Box
            ref={scrollRef}
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              p: 2,
              display: "flex",
              flexDirection: "column",
              gap: 2,
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: "#cbd5e1",
                borderRadius: "10px",
              },
            }}
          >
            {chatHistory.map((msg) => (
              <Box
                key={msg.id}
                sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
              >
                <Box
                  sx={{
                    bgcolor: msg.type === "user" ? "#e0e7ef" : "#ffffff",
                    color: "#334155",
                    borderRadius: "18px 18px 18px 4px",
                    px: 2,
                    py: 1.5,
                    boxShadow: "0 2px 8px rgba(100,116,139,0.07)",
                    border: "1px solid #cbd5e1",
                    display: "flex",
                    gap: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      bgcolor: "#cbd5e1",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <PersonOutlineIcon
                      sx={{ color: "#64748B", fontSize: 20 }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{ color: "#64748B", display: "block", mb: 0.5 }}
                    >
                      {msg.timestamp.toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        wordBreak: "break-word",
                        whiteSpace: "pre-line",
                      }}
                    >
                      {msg.text}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}

            {/* Thinking indicator — shown while a refinement is in progress */}
            {isRefining && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Box
                  sx={{
                    bgcolor: "#ffffff",
                    color: "#334155",
                    borderRadius: "18px 18px 18px 4px",
                    px: 2,
                    py: 1.5,
                    boxShadow: "0 2px 8px rgba(100,116,139,0.07)",
                    border: "1px solid #cbd5e1",
                    display: "flex",
                    gap: 1.5,
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      bgcolor: "#e0e7ef",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <CircularProgress size={16} sx={{ color: "#4F6AF0" }} />
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ color: "#64748B", fontStyle: "italic" }}
                  >
                    Updating workflow…
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          {/* Left Panel: Input Area */}
          <Box
            sx={{ p: 2, bgcolor: "#f8fafc", borderTop: "1px solid #e2e8f0" }}
          >
            {/* Indeterminate progress bar — visible while LLM call is in flight */}
            {isRefining && (
              <LinearProgress
                variant="indeterminate"
                sx={{
                  mb: 1.5,
                  borderRadius: 99,
                  height: 3,
                  bgcolor: "rgba(79,106,240,0.12)",
                  "& .MuiLinearProgress-bar": { bgcolor: "#4F6AF0" },
                }}
              />
            )}
            <TextField
              fullWidth
              label={isEdit ? "What should change?" : "Workflow Refinement"}
              placeholder={
                isEdit
                  ? "E.g. 'Add a Slack notification step at the end'"
                  : "Optional: suggest a change to the workflow"
              }
              variant="filled"
              multiline
              minRows={3}
              maxRows={6}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey && !isEdit) {
                  e.preventDefault();
                  handleSendRefinement();
                }
              }}
              InputLabelProps={{
                shrink: true,
                sx: {
                  color: "#8c7d70",
                  fontWeight: 500,
                  transform: "translate(12px, 10px) scale(0.75)",
                  "&.Mui-focused": { color: "#bc8e5c" },
                },
              }}
              InputProps={{
                disableUnderline: true,
                ...(!isEdit && {
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      sx={{
                        alignSelf: "flex-end",
                        mb: 1,
                        alignItems: "center",
                      }}
                    >
                      <Button
                        variant="contained"
                        onClick={handleSendRefinement}
                        disabled={
                          !inputValue || !inputValue.trim() || isRefining
                        }
                        sx={{
                          position: "absolute",
                          bottom: "12px",
                          right: "12px",
                          minWidth: "36px",
                          width: "36px",
                          height: "36px",
                          borderRadius: "45%",
                          padding: 0,
                          bgcolor: "#bc8e5c",
                          boxShadow: "0 4px 12px rgba(188, 142, 92, 0.2)",
                          "&:hover": { bgcolor: "#a37a4f" },
                          "&.Mui-disabled": {
                            bgcolor: "#efeae6",
                            color: "#cbd5e1",
                          },
                          zIndex: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {isRefining ? (
                          <CircularProgress size={18} sx={{ color: "white" }} />
                        ) : (
                          <ArrowUpwardIcon sx={{ fontSize: 22 }} />
                        )}
                      </Button>
                    </InputAdornment>
                  ),
                }),
                sx: {
                  borderRadius: "12px",
                  bgcolor: "#f7f3f0",
                  padding: "24px 12px 8px 12px",
                  border: "1px solid transparent",
                  "&:hover": { bgcolor: "#f2ede9" },
                  "&.Mui-focused": { border: "1px solid #bc8e5c" },
                  "& .MuiInputBase-input": { paddingTop: "12px" },
                },
              }}
            />
            {/* Edit mode: explicit full-width Update button so the action is unambiguous */}
            {isEdit && (
              <Button
                fullWidth
                variant="contained"
                onClick={handleSendRefinement}
                disabled={!inputValue.trim() || isRefining}
                startIcon={
                  isRefining ? (
                    <CircularProgress size={16} sx={{ color: "white" }} />
                  ) : undefined
                }
                sx={{
                  mt: 1.5,
                  borderRadius: 2,
                  fontWeight: 700,
                  py: 1.2,
                  bgcolor: "#bc8e5c",
                  boxShadow: "0 4px 12px rgba(188, 142, 92, 0.2)",
                  "&:hover": { bgcolor: "#a37a4f" },
                  "&.Mui-disabled": { bgcolor: "#efeae6", color: "#cbd5e1" },
                  textTransform: "none",
                  fontSize: "0.95rem",
                }}
              >
                {isRefining ? "Updating…" : "Update Workflow"}
              </Button>
            )}
          </Box>
        </Box>

        {/* Right Panel: Workflow Content */}
        <Box
          sx={{
            width: "70%",
            flexShrink: 0,
            flexGrow: 0,
            minWidth: 0,
            boxSizing: "border-box",
            px: 3,
            py: 1,
            overflowY: "auto",
            overflowX: "hidden",
            minHeight: 0,
          }}
        >
          <Box sx={{ width: "100%", minWidth: 0 }}>
            {/* Workflow name — always an editable text field, never overwritten by LLM */}
            <Paper
              elevation={0}
              sx={{
                px: 3,
                py: 1,
                mb: 1,
                borderRadius: 4,
                border: "1px solid",
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <TextField
                fullWidth
                label="Workflow Name"
                variant="filled"
                value={nameInputValue}
                onChange={(e) => {
                  setNameInputValue(e.target.value);
                  setNameChanged(
                    e.target.value.trim() !== savedNameRef.current.trim(),
                  );
                  onWorkflowNameChange?.(e.target.value);
                }}
                onKeyDown={async (e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    const trimmed = nameInputValue.trim();
                    if (!trimmed || !isEdit || !onWorkflowNameSave) return;
                    await handleSaveName(trimmed);
                    savedNameRef.current = trimmed;
                    setNameChanged(false);
                  }
                }}
              />
              {/* Edit mode: Save button — always visible, enabled only when name differs from saved */}
              {isEdit &&
                (isSavingName ? (
                  <CircularProgress
                    size={20}
                    sx={{ color: "#bc8e5c", flexShrink: 0 }}
                  />
                ) : (
                  <Button
                    variant="contained"
                    disabled={!nameChanged}
                    onClick={async () => {
                      const trimmed = nameInputValue.trim();
                      if (!trimmed || !onWorkflowNameSave) return;
                      await handleSaveName(trimmed);
                      savedNameRef.current = trimmed;
                      setNameChanged(false);
                    }}
                    sx={{
                      flexShrink: 0,
                      fontWeight: 700,
                      borderRadius: 2,
                      px: 3,
                      textTransform: "none",
                      ...(nameChanged
                        ? {
                            bgcolor: "#bc8e5c",
                            "&:hover": { bgcolor: "#a37a4f" },
                          }
                        : {}),
                    }}
                  >
                    Save
                  </Button>
                ))}
            </Paper>

            <Paper
              elevation={0}
              sx={{
                mb: 1,
                px: 3,
                py: 1.2,
                borderRadius: 4,
                border: "1px solid",
                borderColor: "divider",
                overflow: "hidden",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, fontWeight: 700, color: "#64748B" }}
              >
                Workflow Visualization
              </Typography>
              <Box sx={{ height: "clamp(220px, 35vh, 420px)", position: "relative" }}>
                <WorkflowVisualization
                  dagDefinition={{
                    ...dagDefinition,
                    // Inject live trigger state so the trigger node reflects
                    // real-time add/remove without needing a page reload.
                    webhook_config: webhookConfig ?? draftWebhookConfig ?? undefined,
                    schedule_config: scheduleConfig ?? draftScheduleConfig ?? undefined,
                  }}
                  suggestedScheduleConfig={suggestedScheduleConfig}
                  suggestedWebhookConfig={suggestedWebhookConfig}
                />
              </Box>
            </Paper>

            {/* ── Trigger suggestion banners (create mode only) ─────────── */}
            {!isEdit && suggestedScheduleConfig && (
              <Alert
                severity="info"
                icon={<ScheduleIcon fontSize="small" />}
                sx={{ mb: 1, borderRadius: 3, fontSize: 13 }}
              >
                <strong>Schedule trigger detected:</strong> This workflow will be scheduled to run <em>{suggestedScheduleConfig.description ?? suggestedScheduleConfig.cron_expression}</em> automatically after creation.
              </Alert>
            )}
            {!isEdit && suggestedWebhookConfig && (
              <Alert
                severity="info"
                icon={<CloudQueueIcon fontSize="small" />}
                sx={{ mb: 1, borderRadius: 3, fontSize: 13 }}
              >
                <strong>Webhook trigger detected:</strong> A generic webhook trigger will be created (disabled). After creation, edit the workflow to add your API key or bearer token to activate it.
              </Alert>
            )}

            {/* <Accordion
            elevation={0}
            sx={{
              mb: 3,
              borderRadius: 4,
              border: "1px solid",
              borderColor: "divider",
              overflow: "hidden",
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" fontWeight={700} color="#64748B">
                DAG Definition (JSON)
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <Box
                component="pre"
                sx={{
                  fontSize: "0.75rem",
                  maxHeight: 300,
                  maxWidth: "100%",
                  overflowX: "auto",
                  overflowY: "auto",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-all",
                  bgcolor: "#1e293b",
                  color: "#f8fafc",
                  p: 2,
                  m: 0,
                }}
              >
                {JSON.stringify(dagDefinition, null, 2)}
              </Box>
            </AccordionDetails>
          </Accordion> */}

            {/* ── Triggers ─────────────────────────────────────── */}
            <Paper
              elevation={0}
              sx={{ mb: 1, px: 3, py: 0.5, borderRadius: 4, border: "1px solid", borderColor: "divider" }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#64748B", mb: 0.5 }}>
                Triggers
              </Typography>

              {/* Manual */}
              <Accordion
                elevation={0}
                defaultExpanded={triggerMode === "manual"}
                sx={{ border: "1px solid #E2E8F0", borderRadius: "12px !important", mb: 1.5, "&:before": { display: "none" } }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ borderRadius: "12px" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <PersonOutlineIcon sx={{ color: "#8A5525", fontSize: 20 }} />
                    <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Manual</Typography>
                    <Chip
                      label={triggerMode === "manual" ? "Selected" : "Available"}
                      size="small"
                      sx={{ bgcolor: triggerMode === "manual" ? "#F4E9DE" : "#f1f5f9", color: triggerMode === "manual" ? "#8A5525" : "#64748B", fontWeight: 600, fontSize: 11 }}
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
                  <Typography variant="body2" sx={{ color: "#64748B", mb: 1.5 }}>
                    Run this workflow from the catalog, Casper, or an API call without an automatic trigger.
                  </Typography>
                  <Button
                    variant={triggerMode === "manual" ? "contained" : "outlined"}
                    onClick={() => setTriggerMode("manual")}
                    sx={{ borderRadius: 99, textTransform: "none", fontWeight: 700 }}
                  >
                    {triggerMode === "manual" ? "Manual selected" : "Use Manual"}
                  </Button>
                </AccordionDetails>
              </Accordion>

              {/* Schedule */}
              <Accordion
                elevation={0}
                defaultExpanded={triggerMode === "schedule" || !!scheduleConfig?.enabled}
                sx={{ border: "1px solid #E2E8F0", borderRadius: "12px !important", mb: 1.5, "&:before": { display: "none" } }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ borderRadius: "12px" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <ScheduleIcon sx={{ color: "#5A7D9A", fontSize: 20 }} />
                    <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Schedule Trigger</Typography>
                    {triggerMode === "schedule"
                      ? <Chip label={getScheduleLabel(scheduleCron)} size="small" sx={{ bgcolor: "#eef3f7", color: "#5A7D9A", fontWeight: 600, fontSize: 11 }} />
                      : <Chip label={scheduleConfig?.enabled ? "Configured" : "Not selected"} size="small" sx={{ bgcolor: "#f1f5f9", color: "#94A3B8", fontWeight: 500, fontSize: 11 }} />}
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
                  <FormControlLabel
                    control={<Switch checked={scheduleEnabled} onChange={(e) => setScheduleEnabled(e.target.checked)} size="small" />}
                    label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Enabled</Typography>}
                    sx={{ mb: 1.5 }}
                  />
                  <CronScheduleBuilder value={scheduleCron} onChange={setScheduleCron} showValidation />
                  {scheduleMsg && <Alert severity={scheduleMsg.type} sx={{ mt: 1.5, borderRadius: 2 }}>{scheduleMsg.text}</Alert>}
                  <Box sx={{ display: "flex", gap: 1.5, mt: 2, flexWrap: "wrap", alignItems: "center" }}>
                    <Button
                      variant={triggerMode === "schedule" ? "contained" : "outlined"}
                      onClick={() => setTriggerMode("schedule")}
                      sx={{ borderRadius: 99, textTransform: "none", fontWeight: 700, bgcolor: triggerMode === "schedule" ? "#5A7D9A" : undefined, "&:hover": { bgcolor: triggerMode === "schedule" ? "#4a6a85" : undefined } }}
                    >
                      {triggerMode === "schedule" ? "Schedule selected" : "Use Schedule"}
                    </Button>
                    {isEdit && workflowId ? (
                      <Button
                        variant="contained"
                        onClick={handleSaveSchedule}
                        disabled={scheduleSaving}
                        startIcon={scheduleSaving ? <CircularProgress size={14} sx={{ color: "white" }} /> : undefined}
                        sx={{ borderRadius: 99, textTransform: "none", fontWeight: 700, bgcolor: "#5A7D9A", "&:hover": { bgcolor: "#4a6a85" } }}
                      >
                        {scheduleSaving ? "Saving…" : "Save Schedule"}
                      </Button>
                    ) : (
                      <Typography variant="caption" sx={{ color: "#64748B" }}>
                        This schedule will be applied when you create the workflow.
                      </Typography>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* Webhook */}
              <Accordion
                elevation={0}
                defaultExpanded={triggerMode === "webhook" || !!webhookConfig?.enabled}
                sx={{ border: "1px solid #E2E8F0", borderRadius: "12px !important", "&:before": { display: "none" } }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ borderRadius: "12px" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <CloudQueueIcon sx={{ color: "#2C3E50", fontSize: 20 }} />
                    <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Webhook Trigger</Typography>
                    {triggerMode === "webhook" && webhookEditable.adapter_type
                      ? <Chip
                          label={webhookEnabled ? webhookEditable.adapter_type : `${webhookEditable.adapter_type} (disabled)`}
                          size="small"
                          sx={{ bgcolor: webhookEnabled ? "#f1f5f9" : "#fee2e2", color: webhookEnabled ? "#2C3E50" : "#991b1b", fontWeight: 600, fontSize: 11 }}
                        />
                      : <Chip label={webhookConfig?.adapter_type ? "Configured" : "Not selected"} size="small" sx={{ bgcolor: "#f1f5f9", color: "#94A3B8", fontWeight: 500, fontSize: 11 }} />}
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
                  <FormControlLabel
                    control={<Switch checked={webhookEnabled} onChange={(e) => setWebhookEnabled(e.target.checked)} size="small" />}
                    label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Enabled</Typography>}
                    sx={{ mb: 1.5 }}
                  />
                  <WebhookConfigBuilder
                    value={webhookEditable}
                    onChange={setWebhookEditable}
                    showValidation={shouldValidateWebhook}
                  />
                  {webhookConfig?.enabled && workflowId && (
                    <Typography variant="caption" sx={{ color: "#94A3B8", mt: 1, display: "block" }}>
                      Endpoint: POST /workflow/{workflowId}/webhook
                    </Typography>
                  )}
                  {webhookMsg && <Alert severity={webhookMsg.type} sx={{ mt: 1.5, borderRadius: 2 }}>{webhookMsg.text}</Alert>}
                  <Box sx={{ display: "flex", gap: 1.5, mt: 2, flexWrap: "wrap", alignItems: "center" }}>
                    <Button
                      variant={triggerMode === "webhook" ? "contained" : "outlined"}
                      onClick={() => setTriggerMode("webhook")}
                      disabled={!webhookEditable.adapter_type}
                      sx={{ borderRadius: 99, textTransform: "none", fontWeight: 700, bgcolor: triggerMode === "webhook" ? "#2C3E50" : undefined, "&:hover": { bgcolor: triggerMode === "webhook" ? "#1a2738" : undefined } }}
                    >
                      {triggerMode === "webhook" ? "Webhook selected" : "Use Webhook"}
                    </Button>
                    {isEdit && workflowId ? (
                      <>
                        <Button
                          variant="contained"
                          onClick={handleSaveWebhook}
                          disabled={webhookSaving || !webhookEditable.adapter_type}
                          startIcon={webhookSaving ? <CircularProgress size={14} sx={{ color: "white" }} /> : undefined}
                          sx={{ borderRadius: 99, textTransform: "none", fontWeight: 700, bgcolor: "#2C3E50", "&:hover": { bgcolor: "#1a2738" } }}
                        >
                          {webhookSaving ? "Saving…" : "Save Webhook"}
                        </Button>
                        {webhookConfig?.adapter_type && (
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={handleRemoveWebhook}
                            disabled={webhookRemoving}
                            startIcon={webhookRemoving ? <CircularProgress size={14} /> : undefined}
                            sx={{ borderRadius: 99, textTransform: "none", fontWeight: 700 }}
                          >
                            {webhookRemoving ? "Removing…" : "Remove Webhook"}
                          </Button>
                        )}
                      </>
                    ) : (
                      <Typography variant="caption" sx={{ color: "#64748B" }}>
                        This webhook trigger will be created when you create the workflow.
                      </Typography>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Paper>

            {/* PLACEHOLDER warning — shown when generated steps have no agent assigned */}
            {placeholderSteps.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2, borderRadius: 3 }}>
                <AlertTitle sx={{ fontWeight: 700 }}>
                  Unassigned Agent Steps
                </AlertTitle>
                {placeholderSteps.length === 1
                  ? `Step "${placeholderSteps[0]}" has no agent assigned.`
                  : `${placeholderSteps.length} steps have no agent assigned: ${placeholderSteps.join(", ")}.`}{" "}
                {"Use the refinement chat below to describe the agents you want, or save and assign them later from the workflow edit page."}
              </Alert>
            )}
          </Box>
        </Box>
      </Box>{" "}
      {/* end main content row */}
      {/* Sticky footer: always visible buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          px: 3,
          py: 1,
          borderTop: "1px solid #e2e8f0",
          bgcolor: "#f8fafc",

          flexShrink: 0,
        }}
      >
        <Button
          variant="outlined"
          color="inherit"
          onClick={onCancel}
          sx={{ borderRadius: 99, px: 4, py: 0.5 }}
        >
          Cancel
        </Button>
        <Button
          size="medium"
          variant={isEdit ? "outlined" : "contained"}
          color="primary"
          onClick={onConfirm}
          disabled={isCreating || !workflowName || !workflowName.trim()}
          sx={{ borderRadius: 99, px: 5, py: 0.5, fontWeight: 700 }}
        >
          {isCreating
            ? "Saving..."
            : isEdit
              ? "Back to Dashboard"
              : "Create Workflow"}
        </Button>
      </Box>
    </Box>
  );
};

export default WorkflowRefinementEditor;
